import { useState, useMemo, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Divider,
} from '@mui/material';
import { useReadModelMetadata } from '@/hooks/dashboard/useReadModelMetadata';
import type { WidgetConfig } from '@/hooks/dashboard/useDashboardWidgets';

interface WidgetEditDialogProps {
  open: boolean;
  widget: WidgetConfig | null;
  onClose: () => void;
  onSave: (widgetId: string, updates: Partial<WidgetConfig>) => void;
}

const WIDGET_TYPES = [
  { value: 'kpiCard', label: 'KPI Card' },
  { value: 'lineChart', label: 'Line Chart' },
  { value: 'barChart', label: 'Bar Chart' },
];

export function WidgetEditDialog({ open, widget, onClose, onSave }: WidgetEditDialogProps) {
  const [displayName, setDisplayName] = useState('');
  const [widgetType, setWidgetType] = useState('');
  const [selectedGroupBy, setSelectedGroupBy] = useState('');

  const metadataQuery = useReadModelMetadata(widget?.readModel ?? '');

  const dimensions = useMemo(() => {
    if (!metadataQuery.data) return [];
    const dims = metadataQuery.data.fields.filter((f) => f.isDimension);
    // For daily read models, offer a "Month" groupBy option that redirects
    // to the monthly read model (e.g. SalesDaily → SalesMonthly)
    if (metadataQuery.data.resolution === 'day') {
      dims.push({ name: 'month', type: 'Date', isDimension: true, isMeasure: false, description: 'Month' });
    }
    return dims.sort((a, b) => (a.description || a.name).localeCompare(b.description || b.name));
  }, [metadataQuery.data]);

  // Sync state when widget changes
  useEffect(() => {
    if (widget) {
      setDisplayName(widget.displayName);
      setWidgetType(widget.type);
      setSelectedGroupBy(widget.groupBy ?? widget.dimension ?? '');
    }
  }, [widget]);

  const needsGroupBy = widgetType === 'barChart' || widgetType === 'lineChart';

  const handleSave = () => {
    if (!widget) return;

    const updates: Partial<WidgetConfig> = {
      displayName,
      type: widgetType,
    };

    if (widgetType === 'barChart') {
      updates.groupBy = selectedGroupBy || undefined;
      updates.dimension = undefined;
    } else if (widgetType === 'lineChart') {
      updates.dimension = selectedGroupBy || undefined;
      updates.groupBy = undefined;
    } else {
      updates.groupBy = undefined;
      updates.dimension = undefined;
    }

    // Adjust layout height based on widget type
    if (widgetType === 'kpiCard' && widget.type !== 'kpiCard') {
      updates.layout = { ...widget.layout, h: 1 };
    } else if (widgetType !== 'kpiCard' && widget.type === 'kpiCard') {
      updates.layout = { ...widget.layout, h: 3 };
    }

    onSave(widget.id, updates);
    onClose();
  };

  if (!widget) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Widget</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {widget.readModel && (
            <>
              <Typography variant="body2" color="text.secondary">
                Source: {widget.readModel} / {widget.measure}
              </Typography>
              <Divider />
            </>
          )}
          <TextField
            label="Display Name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            fullWidth
            autoFocus
          />
          <FormControl fullWidth>
            <InputLabel>Widget Type</InputLabel>
            <Select
              value={widgetType}
              label="Widget Type"
              onChange={(e) => {
                setWidgetType(e.target.value);
                setSelectedGroupBy('');
              }}
            >
              {WIDGET_TYPES.map((wt) => (
                <MenuItem key={wt.value} value={wt.value}>
                  {wt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {needsGroupBy && (
            <FormControl fullWidth>
              <InputLabel>Group By</InputLabel>
              <Select
                value={selectedGroupBy}
                label="Group By"
                onChange={(e) => setSelectedGroupBy(e.target.value)}
              >
                {dimensions.map((d) => (
                  <MenuItem key={d.name} value={d.name}>
                    {d.description || d.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!displayName.trim() || (needsGroupBy && !selectedGroupBy)}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
